import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

// eslint-disable-next-line import/order
import { AuthDataService, UZURV_REST_CONFIGURATION, UzurvRestConfiguration, UzurvRestService } from '@uzurv/http';
import { Deserialize } from 'cerialize';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Banner, Driver, Vehicle } from '../../models';

import { BannerService } from '../banner/banner.service';

@Injectable({
  providedIn: 'root'
})
export class DriverService extends UzurvRestService {
  readonly resourceUrl = 'drivers/';

  // Endpoint RegExps
  static readonly LIST_RESERVATIONS_URL_REGEX: RegExp = /drivers\/[0-9]*\/reservations\/?$/;
  static readonly RESERVATION_BY_ID_URL_REGEX: RegExp = /drivers\/[0-9]*\/reservations\/([0-9]*)\/?$/;
  static readonly TOLL_REIMBURSEMENT_URL_REGEX: RegExp = /drivers\/[0-9]*\/reservations\/([0-9]*)\/tollReimbursements\/?$/;
  static readonly CURRENT_DRIVER_STATUS_URL_REGEX: RegExp = /drivers\/[0-9]*\/currentStatus\/?$/;
  static readonly LIST_CANCELLATION_REASONS_URL_REGEX: RegExp = /reservations\/cancellationReasons\/?$/;
  static readonly RIDE_REQUEST_INFO_URL_REGEX: RegExp = /drivers\/[0-9]*\/rideRequests\/([0-9]*)\/rideRequestInfo\/?$/;
  static readonly RESERVATION_PHONE_CALLS_URL_REGEX: RegExp = /drivers\/[0-9]*\/reservations\/([0-9]*)\/phoneCalls\/?$/;
  static readonly RESERVATION_RIDER_POSITION_URL_REGEX: RegExp = /drivers\/[0-9]*\/reservations\/([0-9]*)\/riderPosition\/?$/;
  static readonly TRANSIT_PROGRAMS_URL_REGEX: RegExp = /transitPrograms\/?$/;
  static readonly RULE_VALUES_URL_REGEX: RegExp = /ruleValues\/?$/;
  static readonly GET_RESERVATION_BY_ID: RegExp = /drivers\/[0-9]*\/reservations\/([0-9]*)\/?$/;
  static readonly RESERVATION_STATUS_UPDATE_URL_REGEX: RegExp = /drivers\/[0-9]*\/reservations\/([0-9]*)\/reservationStatus/;
  static readonly DRIVER_POSITION_UPDATE_URL_REGEX: RegExp = /drivers\/[0-9]*\/driverPosition/;
  static readonly SYNC_QUEUED_ROUTE_NODES_URL_REGEX: RegExp = /drivers\/[0-9]*\/queuedRouteNodes/;
  static readonly RIDER_FARE_URL_REGEX: RegExp = /drivers\/[0-9]*\/reservations\/[0-9]*\/riderFares\/?(?:\?multi=true)?/;

  constructor(
    httpClient: HttpClient,
    @Inject(UZURV_REST_CONFIGURATION)
    protected uzurvRestConfiguration: UzurvRestConfiguration,
    domSanitizer: DomSanitizer,
    private bannerService: BannerService,
    private authDataService: AuthDataService
  ) {
    super(httpClient, uzurvRestConfiguration, domSanitizer);
  }

  /*****************  Search ***********************/

  /**
   * Finds a driver by their ID
   *
   * @param driverId
   * @permission driver
   */
  findDriverById(driverId: number): Observable<Driver> {
    return this.get(`${this.resourceUrl}${driverId}`).pipe(map(response => Deserialize(response, Driver)));
  }

  /**
   * Search a driver by arbitrary means. Will match on name, username, ID, etc.
   *
   * @param searchString
   * @param pageNumber
   * @param pageSize
   * @permission driver_search
   */
  searchForDriver(searchString: string, pageNumber = 0, pageSize = 20): Observable<Array<Driver>> {
    return this.get('search/drivers/', {
      searchString: searchString,
      pageNumber: pageNumber,
      pageSize: pageSize
    }).pipe(map(response => Deserialize(response, Driver)));
  }

  getVehicleWithIdForDriver(vehicleId: number, driver: Driver): Vehicle {
    return driver.vehicles.find(vehicle => vehicle.id === vehicleId);
  }

  /************************************* Banners ***********************/

  /***
   * Lists banners for a driver
   *
   * @param driverId
   * @param appBundleIdentifier
   * @param appVersion
   * @param releasePlatformCode
   * @permission driver
   */
  listBannersByApp(
    driverId: number,
    appBundleIdentifier: string,
    appVersion: string,
    releasePlatformCode: string
  ): Observable<Array<Banner>> {
    return this.get<Array<Banner>>(`${this.resourceUrl}${driverId}/banners/`, {
      appBundleIdentifier: appBundleIdentifier,
      appVersion: appVersion,
      releasePlatformCode: releasePlatformCode
    }).pipe(map(response => this.bannerService.removeUserDismissedBanners(Deserialize(response, Banner))));
  }
}
